import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import type { IframeOptions } from '@atlassian/commerce-experience-sdk/controllers/commerce-experience/types';
import OriginTracer from '@atlassiansox/origin-tracing/origin-tracer';

import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import type { TwcDiscoveryVariant } from '../../..';

import { messages } from './messages';

// The Commerce experience SDK is only needed once the modal opens, so load it on demand.
const loadCommerceExperience = () =>
	import(
		/* webpackChunkName: "twc-discovery-commerce-experience" */ '@atlassian/commerce-experience-sdk-react/experience-sdk-react'
	);
const LazyCommerceExperience = lazy(loadCommerceExperience);

/** Starts loading the Commerce experience SDK ahead of a likely CTA click. */
export const preloadCommerceExperience = (): void => {
	void loadCommerceExperience();
};

const ORDER_SUCCESS_SCREEN_COMPLETED = 'orderSuccessScreen';

const styles = cssMap({
	modal: {
		position: 'relative',
		overflow: 'hidden',
		width: '100%',
		height: '100%',
		borderRadius: token('radius.xlarge'),
	},
	closeButton: {
		position: 'absolute',
		top: token('space.300'),
		right: token('space.300'),
		zIndex: 1,
	},
	loading: {
		position: 'absolute',
		top: '0',
		left: '0',
		width: '100%',
		height: '100%',
		backgroundColor: token('elevation.surface.overlay'),
	},
});

type TwcDiscoveryModalProps = {
	experienceUrl: string;
	variant: TwcDiscoveryVariant;
	onClose: () => void;
};

export const TwcDiscoveryModal = ({
	experienceUrl,
	variant,
	onClose,
}: TwcDiscoveryModalProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();

	// A fresh origin for each modal open, shared by the iframe URL and the viewed event.
	const originTracer = useMemo(() => new OriginTracer({ product: 'admin' }), []);

	const iframeUrl = useMemo(() => {
		const url = new URL(originTracer.addToUrl(experienceUrl));
		url.searchParams.set('disablePadding', 'true');
		url.searchParams.set('orderSuccessShowCloseButton', 'true');
		return url.toString();
	}, [experienceUrl, originTracer]);

	const configuration = useMemo<IframeOptions['configuration']>(
		() => ({ theme: colorMode }),
		[colorMode],
	);

	const hasReportedView = useRef(false);
	useEffect(() => {
		if (hasReportedView.current) {
			return;
		}
		hasReportedView.current = true;
		fireAnalyticsEvent({
			eventType: 'screen',
			name: 'twcDiscoveryModal',
			attributes: {
				variant,
				...originTracer.toAnalyticsAttributes({ hasGeneratedId: true }),
			},
		});
	}, [fireAnalyticsEvent, originTracer, variant]);

	// Covers both the SDK chunk loading and the iframe loading.
	const [isLoading, setIsLoading] = useState(true);
	const handleIframeLoad = useCallback(() => setIsLoading(false), []);

	const hasCompleted = useRef(false);
	const handleIframeComplete = useCallback(() => {
		if (hasCompleted.current) {
			return;
		}
		hasCompleted.current = true;
		fireAnalyticsEvent({
			eventType: 'track',
			action: 'completed',
			actionSubject: 'twcDiscoveryPurchase',
			attributes: { variant, ...originTracer.toAnalyticsAttributes() },
		});
	}, [fireAnalyticsEvent, originTracer, variant]);

	const handleClose = useCallback(() => {
		// Closing after a purchase isn't drop-off.
		if (!hasCompleted.current) {
			fireAnalyticsEvent({
				eventType: 'ui',
				action: 'closed',
				actionSubject: 'modal',
				actionSubjectId: 'twcDiscoveryModal',
				attributes: {
					variant,
					hasLoaded: !isLoading,
					...originTracer.toAnalyticsAttributes(),
				},
			});
		}
		onClose();
	}, [fireAnalyticsEvent, isLoading, onClose, originTracer, variant]);

	const handleComplete = useCallback(
		(data: { name: string }) => {
			if (data.name === ORDER_SUCCESS_SCREEN_COMPLETED) {
				handleClose();
				return;
			}
			handleIframeComplete();
		},
		[handleClose, handleIframeComplete],
	);

	const label = formatMessage(messages.modalLabel);

	return (
		<Modal
			onClose={handleClose}
			width={968}
			height="640px"
			shouldScrollInViewport={false}
			label={label}
		>
			<Box xcss={styles.modal}>
				<Box xcss={styles.closeButton}>
					<CloseButton onClick={handleClose} />
				</Box>
				<Suspense fallback={null}>
					<LazyCommerceExperience
						title={label}
						url={iframeUrl}
						// Without this the SDK rewrites the URL to the in-product /admin-embed path.
						useFullUrl
						configuration={configuration}
						testId="twc-discovery-modal"
						onLoad={handleIframeLoad}
						onError={handleIframeLoad}
						onComplete={handleComplete}
					/>
				</Suspense>
				{isLoading && (
					<Flex xcss={styles.loading} alignItems="center" justifyContent="center">
						<Spinner size="large" label={formatMessage(messages.modalLoading)} />
					</Flex>
				)}
			</Box>
		</Modal>
	);
};
